import {
  put,
  putResolve,
  takeLatest,
  takeEvery,
  select,
  all,
} from 'redux-saga/effects';
import {
  TeammoApi,
  normalize,
  compilePaginationObject,
} from 'app/utils/redux/helpers';
import { teamConnectionSchema, teamConnectionListSchema } from 'app/utils/redux/schemas';
import { handleError } from 'app/utils/handlers/handleError';
import {
  loadingStart,
  loadingFinish,
  actionConfirmed,
} from 'app/ducks/loading/actions';
import {
  sagaActions,
} from 'app/ducks/teamConnections/actions';
import {
  getTeam,
} from 'app/ducks/teams/actions';

import { TeamConnectionsActionTypes as types } from 'app/ducks/teamConnections/types';

function* createTeamConnectionSaga(action): any {
  yield put(sagaActions.createTeamConnectionPending(action.payload));
  const [error, response] = yield (yield TeammoApi(yield select())).teamConnections.create(
    action.payload.data,
  );

  if (response) {
    const normalizedResponse = normalize(response, teamConnectionSchema);

    yield put(sagaActions.createTeamConnectionSuccess(normalizedResponse));
    yield putResolve(yield getTeam(action.payload.data.team.id));

    if (action.payload.callbackFunction !== undefined) {
      action.payload.callbackFunction(
        undefined,
        normalizedResponse.entities?.teamConnections[normalizedResponse.result],
      );
    }
  } else {
    yield put(
      handleError(
        error,
        sagaActions.createTeamConnectionFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* updateTeamConnectionSaga(action): any {
  yield put(sagaActions.updateTeamConnectionPending(action.payload));
  const [error, response] = yield (yield TeammoApi(yield select())).teamConnections.update(
    action.payload.data,
  );

  if (response) {
    const normalizedResponse = normalize(response, teamConnectionSchema);

    yield put(sagaActions.updateTeamConnectionSuccess(normalizedResponse));
    yield putResolve(
      yield getTeam(
        normalizedResponse.entities?.teamConnections[normalizedResponse.result]
          .team?.id,
      ),
    );

    if (action.payload.callbackFunction !== undefined) {
      action.payload.callbackFunction(
        undefined,
        normalizedResponse.entities?.teamConnections[normalizedResponse.result],
      );
    }
  } else {
    yield put(
      handleError(
        error,
        sagaActions.updateTeamConnectionFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* deleteTeamConnectionSaga(action): any {
  yield put(sagaActions.deleteTeamConnectionPending(action.payload.id));
  const [error, response] = yield (yield TeammoApi(yield select())).teamConnections.delete(
    action.payload,
  );

  if (response || _.isEmpty(error)) {
    // const normalizedResponse = normalize(response, teamConnectionSchema);
    yield put(actionConfirmed('Team connection deleted'));
    yield put(sagaActions.deleteTeamConnectionSuccess(action.payload.id));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.deleteTeamConnectionFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  }
}

function* getTeamConnectionSaga(action): any {
  yield put(sagaActions.getTeamConnectionPending());
  const [error, response] = yield (yield TeammoApi(yield select())).teamConnections.show(
    action.payload,
  );
  if (response) {
    const normalizedResponse = normalize(response, teamConnectionSchema);
    yield put(sagaActions.getTeamConnectionSuccess(normalizedResponse));
    return normalizedResponse.entities.teams[normalizedResponse.result];
  }
  yield put(
    handleError(error, sagaActions.getTeamConnectionFailed(_.isEmpty(error) ? {} : error.response.data.errors)),
  );
  return null;
}

function* getAllTeamConnectionsSaga(action): any {
  yield put(
    sagaActions.getAllTeamConnectionsPending(action.payload.compile ? action.payload.pagination : {}),
  );
  yield put(loadingStart());

  const paginationObject = action.payload.compile
    ? compilePaginationObject(yield select((state) => state.teamConnections.pagination))
    : action.payload.pagination;
  let teammoApi = TeammoApi(yield select());

  if (action.payload.teamConnectionView) {
    teammoApi = TeammoApi(yield select(), null, null, false, false, {
      'team-connection-code': action.payload.teamConnectionCode,
      'team-code': action.payload.teamCode,
    });
  }
  const [error, response] = yield (yield teammoApi).teamConnections.list(
    paginationObject,
  );

  if (response) {
    const normalizedResponse = normalize(response, teamConnectionListSchema);

    yield put(
      sagaActions.getAllTeamConnectionsSuccess(normalizedResponse, response.meta),
    );
    yield put(loadingFinish());
  } else {
    yield put(error, sagaActions.getAllTeamConnectionsFailed(_.isEmpty(error) ? {} : error.response.data.errors));
  }
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createTeamConnectionSagaWatcher(): any {
  yield takeLatest(types.CREATE_TEAM_CONNECTION_REQUESTED, createTeamConnectionSaga);
}

function* updateTeamConnectionSagaWatcher(): any {
  yield takeLatest(types.UPDATE_TEAM_CONNECTION_REQUESTED, updateTeamConnectionSaga);
}

function* deleteTeamConnectionSagaWatcher(): any {
  yield takeLatest(types.DELETE_TEAM_CONNECTION_REQUESTED, deleteTeamConnectionSaga);
}

function* getTeamConnectionSagaWatcher(): any {
  yield takeLatest(types.GET_TEAM_CONNECTION_REQUESTED, getTeamConnectionSaga);
}

function* getAllTeamConnectionsSagaWatcher(): any {
  yield takeLatest(types.GET_ALL_TEAM_CONNECTIONS_REQUESTED, getAllTeamConnectionsSaga);
}

export default function* root(): any {
  yield all([
    createTeamConnectionSagaWatcher(),
    updateTeamConnectionSagaWatcher(),
    deleteTeamConnectionSagaWatcher(),
    getTeamConnectionSagaWatcher(),
    getAllTeamConnectionsSagaWatcher(),
  ]);
}
